import {
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { FaEdit } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';
import { IoMdAdd } from 'react-icons/io';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Form } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import { Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { addFormField, getFormFields, updateFormField, deleteFormField } from '../../../redux/actions/Formfields.action';

const FormFields = () => {
  const dispatch = useDispatch();
  const formFields = useSelector((state) => state.formFields);
  const [isLoading, setIsLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [updateModal, setUpdateModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedField, setSelectedField] = useState(null);

  const handleAddModalClose = () => setShow(false);
  const handleAddModalOpen = () => setShow(true);

  const handleUpdateModalClose = () => {
    setUpdateModal(false);
    setSelectedField(null);
  };

  const handleUpdateModalOpen = (field) => {
    setSelectedField(field);
    setUpdateModal(true);
  };

  const handleDeleteModalClose = () => {
    setDeleteModal(false);
    setSelectedField(null);
  };

  const handleDeleteModalOpen = (field) => {
    setSelectedField(field);
    setDeleteModal(true);
  };

  const initialValues = {
    name: '',
    label: '',
    inputType: '',
    required: false,
    options: [],
    defaultValue: '',
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required('Name is required')
      .matches(/^[a-zA-Z0-9 _-]+$/, 'Special characters are not allowed in Name'),
    label: Yup.string()
      .required('Label is required')
      .matches(/^[a-zA-Z0-9 _-]+$/, 'Special characters are not allowed in Label'),
    inputType: Yup.string().required('Input type is required'),
    required: Yup.boolean(),
    options: Yup.array().when('inputType', {
      is: (value) => ['select', 'radio', 'checkbox'].includes(value),
      then: () => Yup.array().min(1, 'At least one option is required for this input type'),
    }),
    defaultValue: Yup.mixed(),
  });

  useEffect(() => {
    dispatch(getFormFields());
  }, [dispatch]);

  const handleSubmit = (values, { resetForm }) => {
    setIsLoading(true);
    dispatch(addFormField(values))
      .then(() => {
        resetForm();
        handleAddModalClose();
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleUpdate = (values) => {
    if (selectedField) {
      setIsLoading(true);
      const data = {
        fieldId: selectedField._id,
        field: values,
      };
      dispatch(updateFormField(data))
        .then(() => {
          handleUpdateModalClose();
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const handleDelete = () => {
    if (selectedField) {
      setIsLoading(true);
      const data = {
        fieldId: selectedField._id,
      };
      dispatch(deleteFormField(data))
        .then(() => {
          handleDeleteModalClose();
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const renderFormFields = ({ handleSubmit, setFieldValue, values, handleClose }) => (
    <Form onSubmit={handleSubmit}>
      <Modal.Body>
        <div className="row">
          <div className="col-12">
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={values.name}
                onChange={(e) => setFieldValue('name', e.target.value)}
                placeholder="Enter field name"
                className="shadow-none focus:!border-theme-dark"
              />
              <ErrorMessage name="name" component="div" className="text-danger" />
            </Form.Group>
          </div>
          <div className="col-12">
            <Form.Group className="mb-3">
              <Form.Label>Label</Form.Label>
              <Form.Control
                type="text"
                name="label"
                value={values.label}
                onChange={(e) => setFieldValue('label', e.target.value)}
                placeholder="Enter field label"
                className="shadow-none focus:!border-theme-dark"
              />
              <ErrorMessage name="label" component="div" className="text-danger" />
            </Form.Group>
          </div>
          <div className="col-12">
            <Form.Group className="mb-3">
              <Form.Label>Input Type</Form.Label>
              <Form.Select
                name="inputType"
                value={values.inputType}
                onChange={(e) => {
                  setFieldValue('inputType', e.target.value);
                  if (!['select', 'radio', 'checkbox'].includes(e.target.value)) {
                    setFieldValue('options', []);
                  }
                }}
                className="shadow-none focus:!border-theme-dark"
              >
                <option value="">Select input type</option>
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="date">Date</option>
                <option value="select">Select</option>
                <option value="checkbox">Checkbox</option>
                <option value="radio">Radio</option>
                <option value="textarea">Textarea</option>
              </Form.Select>
              <ErrorMessage name="inputType" component="div" className="text-danger" />
            </Form.Group>
          </div>
          <div className="col-12">
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="required"
                label="Required Field"
                checked={values.required}
                onChange={(e) => setFieldValue('required', e.target.checked)}
                className="shadow-none"
              />
            </Form.Group>
          </div>

          {(['select', 'radio', 'checkbox'].includes(values.inputType)) && (
            <div className="col-12">
              <Form.Group className="mb-3">
                <Form.Label>Options</Form.Label>
                <div className="d-flex gap-2 mb-2">
                  <Form.Control
                    type="text"
                    placeholder="Add new option"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const newOption = e.target.value.trim();
                        if (newOption && !values.options.includes(newOption)) {
                          setFieldValue('options', [...values.options, newOption]);
                          e.target.value = '';
                        }
                      }
                    }}
                    className="shadow-none focus:!border-theme-dark"
                  />
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {values.options.map((option, index) => (
                    <div key={index} className="bg-gray-100 px-3 py-1 rounded-pill d-flex align-items-center gap-2">
                      <span>{option}</span>
                      <MdDelete
                        className="cursor-pointer text-danger"
                        onClick={() => {
                          const newOptions = values.options.filter((_, i) => i !== index);
                          setFieldValue('options', newOptions);
                        }}
                      />
                    </div>
                  ))}
                </div>
                <ErrorMessage name="options" component="div" className="text-danger" />
                <small className="text-muted">Press Enter to add an option</small>
              </Form.Group>
            </div>
          )}

          {values.inputType && values.inputType !== 'checkbox' && (
            <div className="col-12">
              <Form.Group className="mb-3">
                <Form.Label>Default Value</Form.Label>
                {values.inputType === 'select' || values.inputType === 'radio' ? (
                  <Form.Select
                    name="defaultValue"
                    value={values.defaultValue || ''}
                    onChange={(e) => setFieldValue('defaultValue', e.target.value)}
                    className="shadow-none focus:!border-theme-dark"
                  >
                    <option value="">Select default value</option>
                    {values.options.map((option, index) => (
                      <option key={index} value={option}>
                        {option}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <Form.Control
                    type={values.inputType === 'textarea' ? 'text' : values.inputType}
                    name="defaultValue"
                    value={values.defaultValue || ''}
                    onChange={(e) => setFieldValue('defaultValue', e.target.value)}
                    as={values.inputType === 'textarea' ? 'textarea' : 'input'}
                    placeholder="Enter default value"
                    className="shadow-none focus:!border-theme-dark"
                  />
                )}
              </Form.Group>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleClose}
          className="px-4 bg-transparent border-[1px] text-black border-green-color"
        >
          Close
        </Button>
        <Button
          type="submit"
          className="!bg-theme-dark border-0 px-4"
        >
          {selectedField ? 'Update' : 'Add'}
        </Button>
      </Modal.Footer>
    </Form>
  );

  return (
    <>
      {isLoading && (
        <div className="loaderAdd">
          <div className="loader_line">
            <img
              src={localStorage.getItem('logo')}
              className="w-[200px]"
              alt="Loading..."
            />
          </div>
        </div>
      )}
      <div className="mt-[100px] d-flex justify-content-between">
        <h4 className="font-bold">Form Fields</h4>
        <Button
          onClick={handleAddModalOpen}
          className="!bg-theme-dark fw-bold d-flex align-items-center border-0"
        >
          <IoMdAdd className="me-1" /> Add Field
        </Button>
      </div>

      <div className="bg-white rounded-[10px] mt-4 p-4">
        <TableContainer>
          <Table variant="striped" colorScheme="blackAlpha">
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Label</Th>
                <Th>Input Type</Th>
                <Th>Required</Th>
                <Th>Options</Th>
                <Th>Default Value</Th>
                <Th style={{ minWidth: '200px' }} className="text-end">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {formFields?.getFormFields?.data?.formFields?.fields?.map((field) => (
                <Tr key={field._id}>
                  <Td>{field.name}</Td>
                  <Td>{field.label}</Td>
                  <Td>{field.inputType}</Td>
                  <Td>{field.required ? 'Yes' : 'No'}</Td>
                  <Td>
                    {field.options && field.options.length > 0 ? (
                      <div className="d-flex flex-wrap gap-1">
                        {field.options.map((option, index) => (
                          <span
                            key={index}
                            className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm font-medium"
                          >
                            {option}
                          </span>
                        ))}
                      </div>
                    ) : (
                      '-'
                    )}
                  </Td>
                  <Td>{field.defaultValue?.toString() || '-'}</Td>
                  <Td>
                    <div className="d-flex gap-3 justify-end">
                      <FaEdit
                        className="cursor-pointer border-green-color fs-5 text-success"
                        onClick={() => handleUpdateModalOpen(field)}
                        title="update"
                      />
                      <MdDelete
                        className="cursor-pointer border-green-color fs-5 text-danger"
                        onClick={() => handleDeleteModalOpen(field)}
                        title="delete"
                      />
                    </div>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </div>

      <Modal show={show} onHide={handleAddModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Add Form Field</Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {(props) => renderFormFields({ ...props, handleClose: handleAddModalClose })}
        </Formik>
      </Modal>

      <Modal show={updateModal} onHide={handleUpdateModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Update Form Field</Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={selectedField || initialValues}
          validationSchema={validationSchema}
          onSubmit={handleUpdate}
          enableReinitialize
        >
          {(props) => renderFormFields({ ...props, handleClose: handleUpdateModalClose })}
        </Formik>
      </Modal>

      <Modal show={deleteModal} onHide={handleDeleteModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Form Field</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this field?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleDeleteModalClose}
            className="px-4 bg-transparent border-[1px] text-black border-green-color"
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            className="!bg-red-500 border-0 px-4"
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FormFields;